import { useEffect, useMemo, useState } from "react";
import {
    adminListSavedOptions, adminCreateSavedOption, adminUpdateSavedOption, adminDeleteSavedOption,
    adminApplySavedOption, adminRemoveSavedOption,
} from "@/lib/api";
import { useAdminAuth } from "@/lib/adminAuth";

const chip = "font-mono text-[9px] uppercase tracking-[0.18em] border px-2 py-1";
const scopeLabel = (lib) => (lib.scope === "global" ? "All brands" : "This brand");
const strip = ({ library_id, ...g }) => g; // eslint-disable-line no-unused-vars

const ScopeChoice = ({ scope, setScope }) => {
    const { user } = useAdminAuth();
    if (user?.role !== "master_admin") return null;
    return (
        <div className="flex gap-2" data-testid="saved-scope-choice">
            {[["brand", "Save for this brand"], ["global", "Save for all brands"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setScope(v)}
                    className={`${chip} ${scope === v ? "border-[var(--gold)] text-gold bg-[var(--gold)]/10" : "border-[var(--border)] text-muted"}`}
                    data-testid={`saved-scope-${v}`}>{l}</button>
            ))}
        </div>
    );
};

// Inside the item editor: add a saved group with one tap.
export const SavedOptionsPicker = ({ brand, groups, onAdd }) => {
    const [libs, setLibs] = useState([]);
    useEffect(() => { adminListSavedOptions(brand.id).then(setLibs).catch(() => setLibs([])); }, [brand.id]);
    const linked = new Set((groups || []).map((g) => g.library_id).filter(Boolean));
    const available = libs.filter((l) => !linked.has(l.id));
    return (
        <div className="border border-dashed border-[var(--gold)] p-3 mb-3" data-testid="saved-options-picker">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold mb-2">🧩 Saved options — tap to add</div>
            {available.length === 0 ? (
                <p className="text-muted text-[12px]">
                    {libs.length ? "All saved options are already on this item." : "None yet — build an options group below and tap “Save for reuse”."}
                </p>
            ) : (
                <div className="flex flex-wrap gap-2">
                    {available.map((l) => (
                        <button key={l.id} type="button" onClick={() => onAdd({ ...l.group, library_id: l.id })}
                            className={`${chip} border-[var(--border)] hover:border-[var(--gold)] hover:text-gold normal-case`}
                            data-testid={`saved-add-${l.id}`}>
                            + {l.name} <span className="text-muted">· {l.group.options?.length || 0} · {scopeLabel(l)}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// Per-group control inside the item editor: shows link state or saves the group for reuse.
export const SaveGroupControl = ({ brand, group, onLinked, onUnlink }) => {
    const [open, setOpen] = useState(false);
    const [name, setName] = useState(group.name || "");
    const [scope, setScope] = useState("brand");
    const [busy, setBusy] = useState(false);
    if (group.library_id) {
        return (
            <div className="flex items-center justify-between gap-2 mb-2 font-mono text-[10px] uppercase tracking-[0.16em] text-gold" data-testid="saved-linked-badge">
                <span>🔗 Saved option — edit it in “Saved Options” to update every item</span>
                <button type="button" onClick={onUnlink} className="text-muted hover:text-[#ff5246]" data-testid="saved-unlink">Unlink</button>
            </div>
        );
    }
    const save = async () => {
        setBusy(true);
        try {
            const lib = await adminCreateSavedOption({ name, scope, brand_id: brand.id, group: strip(group) });
            onLinked(lib.id);
            setOpen(false);
        } catch (ex) {
            alert(ex.response?.data?.detail || "Could not save");
        } finally {
            setBusy(false);
        }
    };
    return open ? (
        <div className="border border-[var(--gold)] p-2 mb-2 space-y-2" data-testid="save-group-form">
            <input className="input text-sm" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name e.g. Choose your soft drink" data-testid="save-group-name" />
            <ScopeChoice scope={scope} setScope={setScope} />
            <div className="flex gap-2">
                <button type="button" className="btn-primary text-[11px] px-3 py-1.5" disabled={busy || !name.trim()} onClick={save} data-testid="save-group-confirm">{busy ? "Saving…" : "Save"}</button>
                <button type="button" className="btn-ghost text-[11px]" onClick={() => setOpen(false)}>Cancel</button>
            </div>
        </div>
    ) : (
        <button type="button" onClick={() => setOpen(true)} className={`${chip} border-[var(--border)] text-muted hover:text-gold hover:border-[var(--gold)] mb-2`} data-testid="save-group-btn">
            ★ Save for reuse
        </button>
    );
};

const ItemChecklist = ({ items, selected, setSelected, linkedIds }) => {
    const byCat = useMemo(() => {
        const m = {};
        for (const it of items) (m[it.category || "Other"] ||= []).push(it);
        return Object.entries(m);
    }, [items]);
    const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const toggleCat = (list) => setSelected((s) => {
        const n = new Set(s);
        const all = list.every((i) => n.has(i.id));
        list.forEach((i) => (all ? n.delete(i.id) : n.add(i.id)));
        return n;
    });
    return (
        <div className="max-h-[38vh] overflow-y-auto border border-[var(--border)] divide-y divide-[var(--border)]" data-testid="saved-item-checklist">
            {byCat.map(([cat, list]) => (
                <div key={cat} className="p-2">
                    <button type="button" onClick={() => toggleCat(list)} className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold mb-1" data-testid={`saved-cat-${cat}`}>
                        {cat} · tick all
                    </button>
                    {list.map((it) => (
                        <label key={it.id} className="flex items-center gap-2 py-1 text-[13px] cursor-pointer">
                            <input type="checkbox" checked={selected.has(it.id)} onChange={() => toggle(it.id)} data-testid={`saved-item-${it.id}`} />
                            <span className="flex-1 truncate">{it.name}</span>
                            {linkedIds.has(it.id) && <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-gold">using</span>}
                        </label>
                    ))}
                </div>
            ))}
        </div>
    );
};

// Brand-level manager: edit a saved group once, push to many items.
export const SavedOptionsModal = ({ brand, Editor, onClose, onChanged }) => {
    const [libs, setLibs] = useState(null);
    const [activeId, setActiveId] = useState(null);
    const [draft, setDraft] = useState(null); // {name, group, scope}
    const [selected, setSelected] = useState(new Set());
    const [msg, setMsg] = useState("");
    const [busy, setBusy] = useState(false);
    const items = brand.items || [];

    const load = () => adminListSavedOptions(brand.id).then(setLibs).catch(() => setLibs([]));
    useEffect(() => { load(); }, [brand.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const active = libs?.find((l) => l.id === activeId);
    const linkedIds = useMemo(() => new Set(items.filter((it) => (it.modifier_groups || []).some((g) => g.library_id === activeId)).map((it) => it.id)), [items, activeId]);

    const open = (lib) => {
        setActiveId(lib.id);
        setDraft({ name: lib.name, group: lib.group, scope: lib.scope });
        setSelected(new Set(items.filter((it) => (it.modifier_groups || []).some((g) => g.library_id === lib.id)).map((it) => it.id)));
        setMsg("");
    };
    const startNew = () => {
        setActiveId("new");
        setDraft({ name: "", scope: "brand", group: { id: Math.random().toString(36).slice(2, 10), name: "Choose your soft drink", type: "single_required", options: [] } });
        setSelected(new Set());
        setMsg("");
    };
    const run = async (fn) => {
        setBusy(true); setMsg("");
        try { await fn(); } catch (ex) { setMsg(ex.response?.data?.detail || "Something went wrong"); } finally { setBusy(false); }
    };
    const saveDraft = () => run(async () => {
        if (activeId === "new") {
            const lib = await adminCreateSavedOption({ name: draft.name || draft.group.name, scope: draft.scope, brand_id: brand.id, group: strip(draft.group) });
            await load(); setActiveId(lib.id); setMsg("Saved ✓ — now tick items below and tap “Add to selected”.");
        } else {
            const r = await adminUpdateSavedOption(activeId, { name: draft.name, group: strip(draft.group) });
            await load(); onChanged?.(); setMsg(`Saved ✓ — ${r.items_updated} item${r.items_updated === 1 ? "" : "s"} updated automatically.`);
        }
    });
    const apply = () => run(async () => {
        const r = await adminApplySavedOption(activeId, brand.id, [...selected]);
        await load(); onChanged?.(); setMsg(`Added to ${r.items_updated} item${r.items_updated === 1 ? "" : "s"} ✓`);
    });
    const removeFrom = () => run(async () => {
        const r = await adminRemoveSavedOption(activeId, brand.id, [...selected]);
        await load(); onChanged?.(); setMsg(`Removed from ${r.items_updated} item${r.items_updated === 1 ? "" : "s"}.`);
    });
    const del = () => {
        if (!window.confirm(`Delete saved option “${active?.name}”? Items keep their current options but stop updating.`)) return;
        run(async () => { await adminDeleteSavedOption(activeId); setActiveId(null); setDraft(null); await load(); onChanged?.(); });
    };

    return (
        <div className="fixed inset-0 z-50 bg-[var(--bg)]/85 backdrop-blur grid place-items-center p-4 overflow-y-auto" data-testid="saved-options-modal">
            <div className="bg-[var(--surface)] border border-[var(--border)] w-full max-w-4xl my-6">
                <div className="border-b border-[var(--border)] px-6 py-4 flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">// 🧩 Saved options — set up once, reuse everywhere</span>
                    <button onClick={onClose} className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted hover:text-gold" data-testid="saved-options-close">Close ✕</button>
                </div>
                <div className="grid md:grid-cols-[240px_1fr]">
                    <aside className="border-r border-[var(--border)] p-4 space-y-1">
                        <button onClick={startNew} className="btn-primary w-full mb-3" data-testid="saved-new-btn">+ New saved option</button>
                        {libs === null ? <p className="text-muted text-sm">Loading…</p> : libs.length === 0 ? (
                            <p className="text-muted text-[12px]">Nothing saved yet. Create one, or open any item and tap “★ Save for reuse” on its options.</p>
                        ) : libs.map((l) => (
                            <button key={l.id} onClick={() => open(l)}
                                className={`w-full text-left px-3 py-2 border ${activeId === l.id ? "border-[var(--gold)] bg-[var(--gold)]/10" : "border-transparent hover:border-[var(--border)]"}`}
                                data-testid={`saved-lib-${l.id}`}>
                                <div className="text-[13px] truncate">{l.name}</div>
                                <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-muted">{scopeLabel(l)} · used on {l.used_by}</div>
                            </button>
                        ))}
                    </aside>
                    <section className="p-5">
                        {!draft ? (
                            <p className="text-muted text-sm">Pick a saved option on the left, or create a new one.</p>
                        ) : (
                            <div className="space-y-4">
                                <input className="input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Saved option name (e.g. Choose your soft drink)" data-testid="saved-name-input" />
                                {activeId === "new" && <ScopeChoice scope={draft.scope} setScope={(scope) => setDraft({ ...draft, scope })} />}
                                <Editor groups={[draft.group]} onChange={(gs) => gs[0] && setDraft({ ...draft, group: gs[0] })} hideAdd />
                                <div className="flex flex-wrap items-center gap-3">
                                    <button className="btn-primary" onClick={saveDraft} disabled={busy || !draft.group.options?.length} data-testid="saved-save-btn">
                                        {activeId === "new" ? "Save option" : "Save & update all items"}
                                    </button>
                                    {activeId !== "new" && <button className="btn-ghost text-[#ff5246]" onClick={del} data-testid="saved-delete-btn">Delete</button>}
                                </div>
                                {activeId !== "new" && (
                                    <div>
                                        <p className="eyebrow mb-2">// Use on these {brand.name} items</p>
                                        <ItemChecklist items={items} selected={selected} setSelected={setSelected} linkedIds={linkedIds} />
                                        <div className="flex flex-wrap gap-3 mt-3">
                                            <button className="btn-primary" disabled={busy || !selected.size} onClick={apply} data-testid="saved-apply-btn">Add to selected ({selected.size})</button>
                                            <button className="btn-secondary" disabled={busy || !selected.size} onClick={removeFrom} data-testid="saved-remove-btn">Remove from selected</button>
                                        </div>
                                    </div>
                                )}
                                {msg && <p className="font-mono text-[12px] text-gold" data-testid="saved-msg">{msg}</p>}
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
};
